/**
 * NoteInputModal.js
 *
 * Big-button note input modal for voyage log entries.
 * Designed for glove operation with 56px minimum tap targets.
 *
 * FT-284: now hosts <InlineTimeField> above the action row in BOTH paths
 * (standalone note AND annotation — Masters may backdate annotations to the
 * moment they noticed the thing being annotated). Submit passes
 * (text, linkedEntryId, timeOptions) up to the parent. Modal stays open on
 * boundary error.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, MessageSquare } from 'lucide-react';
import { useInlineEventTime } from './useInlineEventTime';
import InlineTimeField from './InlineTimeField';
import ModalErrorBanner from './ModalErrorBanner';

// Quick note templates - pre-fill text with cursor at end for user to add detail
const QUICK_NOTES = [
  { label: 'Watch Handover', value: 'Watch handover to: ' },
  { label: 'GMDSS Log', value: 'GMDSS: ' },
  { label: 'Alarm/Detector', value: 'Alarm: ' },
  { label: 'Event', value: 'Event: ' },
];

const NoteInputModal = ({
  isOpen,
  onClose,
  onSubmit,
  linkedEntry = null,  // If provided, this note is an annotation on an existing entry
  vesselTimezone
}) => {
  // FT-284: UNCONDITIONAL hook calls — Rules-of-Hooks safe.
  const timeField = useInlineEventTime(vesselTimezone);

  const [noteText, setNoteText] = useState('');

  // FT-284: boundary error + submit-in-flight guard.
  const [boundaryError, setBoundaryError] = useState(null);
  const submitInProgressRef = useRef(false);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setNoteText('');
      setBoundaryError(null);
      timeField.reset();
    }
    // timeField.reset is a stable useCallback; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!noteText.trim()) return;
    if (!timeField.isValid || submitInProgressRef.current) return;
    submitInProgressRef.current = true;
    setBoundaryError(null);
    try {
      const timeOptions = timeField.freshSnapshot();
      const result = await onSubmit(
        noteText.trim(),
        linkedEntry?.id || null,
        timeOptions
      );
      if (result?.boundaryError) {
        setBoundaryError(result.boundaryError);
        return;
      }
      setNoteText('');
      onClose();
    } finally {
      submitInProgressRef.current = false;
    }
  };

  const handleQuickNote = (value) => {
    setNoteText(value);
    // Focus textarea and move cursor to end
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(value.length, value.length);
      }
    }, 0);
  };

  const handleCancel = () => {
    setNoteText('');
    onClose();
  };

  const isAnnotation = !!linkedEntry;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleCancel} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Dark style matching WeatherInputModal */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500 rounded-lg">
              {isAnnotation ? (
                <MessageSquare className="h-5 w-5 text-white" />
              ) : (
                <FileText className="h-5 w-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {isAnnotation ? 'Add Annotation' : 'Add Note'}
              </h2>
              <p className="text-sm text-slate-300">
                {isAnnotation ? 'Comment on log entry' : 'Add to voyage log'}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        {/* Linked entry preview */}
        {isAnnotation && linkedEntry && (
          <div className="mx-4 mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Annotating:</p>
            <p className="text-sm text-slate-700 font-medium">
              {linkedEntry.text || linkedEntry.type || 'Log entry'}
            </p>
          </div>
        )}

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Quick note buttons - only show for standalone notes */}
          {!isAnnotation && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Quick notes
              </label>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_NOTES.map((note) => (
                  <button
                    key={note.label}
                    onClick={() => handleQuickNote(note.value)}
                    className={`
                      min-h-[56px] px-3 py-2 rounded-lg border-2 transition-all
                      text-sm font-medium text-left
                      ${noteText === note.value
                        ? 'bg-cyan-600 border-cyan-700 ring-4 ring-cyan-300 text-white'
                        : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                      }
                    `}
                  >
                    {note.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Text input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {isAnnotation ? 'Your annotation' : 'Or type your note'}
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={isAnnotation ? "Add your annotation..." : "Enter your note..."}
              rows={4}
              autoFocus={isAnnotation}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none text-base"
            />
          </div>
        </div>

        {/* FT-283 §G: inline time field + boundary banner sit just above the
            action row so they remain visible after the user scrolls. */}
        <div className="px-4 pt-3 pb-1 space-y-3 bg-white">
          {boundaryError && (
            <ModalErrorBanner message={boundaryError.humanMessage} />
          )}
          <InlineTimeField
            timeField={timeField}
            vesselTimezone={vesselTimezone}
          />
        </div>

        {/* Actions - 56px buttons */}
        <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={handleCancel}
            className="flex-1 py-3 px-4 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!noteText.trim() || !timeField.isValid}
            className="flex-1 py-3 px-4 min-h-[56px] bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAnnotation ? 'Add Annotation' : 'Add Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoteInputModal;
